import React, { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Check, Mail, MapPin, Phone, User, ArrowRight, FlaskConical, Gem, ShieldCheck, Loader2 } from "lucide-react";

const PageHead = ({ eyebrow, title, sub }) => (
  <div className="relative border-b border-[color:var(--line)] overflow-hidden noise">
    <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(60% 100% at 50% 0%, rgba(155,92,255,0.25), transparent 60%)" }} />
    <div className="relative mx-auto max-w-[1400px] px-5 py-16">
      {eyebrow && <p className="eyebrow mb-4">{eyebrow}</p>}
      <h1 className="h-display text-5xl sm:text-6xl text-chrome">{title}</h1>
      {sub && <p className="text-[color:var(--chrome-2)] mt-5 max-w-2xl">{sub}</p>}
    </div>
  </div>
);

export const About = () => (
  <div className="bg-ink min-h-screen">
    <PageHead eyebrow="Our Story" title="BETTER CARDS. BETTER COLLECTIONS."
      sub="CardScienceHQ.com is the ecosystem. CARD HQ is what collectors know — a premium marketplace and a lab-grade card-care operation built on trust." />
    <div className="mx-auto max-w-[900px] px-5 py-16 space-y-8 text-[color:var(--chrome-2)] leading-relaxed">
      <p>We built Card HQ for collectors who care about condition. Every CardScience Select™ card is individually selected, inspected and photographed — so you're buying a specific, documented copy, not an unknown raw card.</p>
      <p>On the service side, The Card Lab provides inspection, professional card care, photography and grading preparation. We never promise a specific grade, and card care does not guarantee a higher grade — we promise documented, professional handling and complete transparency.</p>
      <div className="grid sm:grid-cols-3 gap-6 pt-6">
        {[[Gem, "Premium Selection"], [FlaskConical, "Lab-Grade Care"], [ShieldCheck, "Collector Trust"]].map(([Icon, t]) => (
          <div key={t} className="rounded-2xl border border-[color:var(--line)] p-6 text-center">
            <Icon className="text-holo mx-auto mb-3" size={26} />
            <p className="text-white font-display font-bold">{t}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const TIERS = [
  { name: "Inspection", price: "from $9", feats: ["Surface / corners / edges review", "Centering review", "Front & back photography", "Digital condition report"] },
  { name: "Card Care", price: "from $19", feats: ["Everything in Inspection", "Professional surface care", "Safe, proven techniques", "Protective holder"], featured: true },
  { name: "Grading Prep", price: "from $29", feats: ["Everything in Card Care", "Documented preparation", "Submission-ready packaging", "Priority turnaround options"] },
];

export const Pricing = () => (
  <div className="bg-ink min-h-screen">
    <PageHead eyebrow="Transparent Pricing" title="CARD LAB PRICING"
      sub="Simple per-card service pricing. Final quotes depend on card count and condition." />
    <div className="mx-auto max-w-[1100px] px-5 py-16 grid md:grid-cols-3 gap-6">
      {TIERS.map((t) => (
        <div key={t.name} className={`rounded-3xl p-8 ${t.featured ? "holo-border holo-glow" : "border border-[color:var(--line)]"}`}>
          <p className="eyebrow mb-3">{t.name}</p>
          <p className="text-3xl font-display font-black text-holo mb-6">{t.price}</p>
          <ul className="space-y-3 mb-8">
            {t.feats.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-[color:var(--chrome-1)]"><Check size={15} className="text-[color:var(--cyan)]" /> {f}</li>)}
          </ul>
          <Link to="/card-lab" className={`block text-center rounded-xl py-3.5 text-sm ${t.featured ? "btn-holo" : "btn-ghost"}`}>GET STARTED</Link>
        </div>
      ))}
    </div>
    <p className="text-center text-xs text-[color:var(--chrome-3)] pb-16 px-5">We never promise a specific grade. Card care does not guarantee a higher grade.</p>
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent — we'll be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const input = "bg-[color:var(--charcoal)] border border-[color:var(--line)] rounded-xl px-4 py-3.5 text-white outline-none focus:border-[color:var(--cyan)] w-full placeholder:text-[color:var(--chrome-3)]";

  return (
    <div className="bg-ink min-h-screen">
      <PageHead eyebrow="We're Here To Help" title="CONTACT CARD HQ" sub="Questions about a submission, our process or CardScience After Hours? Reach out." />
      <div className="mx-auto max-w-[900px] px-5 py-16 grid md:grid-cols-2 gap-10">
        <div className="space-y-5">
          {[[Mail, "chunnchris593@gmail.com", "mailto:chunnchris593@gmail.com"], [Phone, "(469) 673-8245", "tel:+14696738245"], [MapPin, "Card HQ Lab — shipping address provided at submission", null]].map(([Icon, t, href]) => (
            <div key={t} className="flex items-center gap-4 rounded-2xl border border-[color:var(--line)] p-5">
              <Icon className="text-[color:var(--cyan)]" size={20} />
              {href ? (
                <a href={href} data-testid={href.startsWith("tel:") ? "contact-phone-link" : "contact-email-link"} className="text-[color:var(--chrome-1)] text-sm hover:text-white transition-colors">{t}</a>
              ) : (
                <span className="text-[color:var(--chrome-1)] text-sm">{t}</span>
              )}
            </div>
          ))}
        </div>
        {sent ? (
          <div data-testid="contact-success" className="glass holo-border rounded-3xl p-8 text-center flex flex-col items-center justify-center">
            <Check size={34} className="text-holo mb-3" />
            <p className="h-display text-xl text-chrome">MESSAGE SENT</p>
            <p className="text-sm text-[color:var(--chrome-2)] mt-2">Thanks for reaching out — we'll get back to you soon.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="glass rounded-3xl p-7 space-y-4">
            <input data-testid="contact-name" className={input} placeholder="Your name" value={form.name} onChange={set("name")} />
            <input data-testid="contact-email" type="email" className={input} placeholder="Email" value={form.email} onChange={set("email")} />
            <textarea data-testid="contact-message" rows={4} className={input} placeholder="How can we help?" value={form.message} onChange={set("message")} />
            <button type="submit" data-testid="contact-submit" disabled={busy}
              className="btn-holo w-full text-center rounded-xl py-3.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <><Loader2 size={16} className="animate-spin" /> SENDING…</> : "SEND MESSAGE"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export const Account = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const rows = [["Username", user?.username], ["Email", user?.email], ["Collector handle", user?.collector_handle || "—"],
    ["Visibility", user?.is_anonymous ? "Anonymous" : "Private by design"],
    ["Member since", user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"]];
  return (
    <div className="bg-ink min-h-[70vh] px-5 py-16" data-testid="account-page">
      <div className="mx-auto max-w-[640px]">
        <div className="flex items-center gap-4 mb-8">
          <div className="h-14 w-14 shrink-0 rounded-full flex items-center justify-center holo-border"><User className="text-white" size={24} /></div>
          <div className="min-w-0">
            <p className="eyebrow">Your Account</p>
            <h1 data-testid="account-title" className="h-display text-3xl text-chrome truncate">{user?.username}</h1>
          </div>
        </div>
        <div className="glass rounded-3xl divide-y divide-[color:var(--line)]">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 px-6 py-4 text-sm">
              <span className="text-[color:var(--chrome-3)] uppercase tracking-wider text-xs">{k}</span>
              <span data-testid={`account-${k.toLowerCase().replace(/ /g, "-")}`} className="text-white text-right break-all">{v}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <Link to="/vault" data-testid="account-vault-link" className="btn-holo rounded-xl px-6 py-3.5 text-sm inline-flex items-center justify-center gap-2">OPEN MY VAULT <ArrowRight size={15} /></Link>
          <Link to="/submit" data-testid="account-submit-link" className="btn-ghost rounded-xl px-6 py-3.5 text-sm text-center">SUBMIT CARDS</Link>
          <button data-testid="account-logout-btn" onClick={() => { logout(); navigate("/"); }} className="btn-ghost rounded-xl px-6 py-3.5 text-sm sm:ml-auto">SIGN OUT</button>
        </div>
      </div>
    </div>
  );
};
